import { useEffect, useState, type MouseEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { IS_DESKTOP, RESEND_COUNTDOWN_SECONDS } from 'app/modules/service/utils/constants';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

interface ResendCodeCountdownProps {
  onResend: () => void;
  resetSignal: number;
  isMaxAttemptsReached: boolean;
}

const ResendCodeCountdown = ({
  onResend,
  resetSignal,
  isMaxAttemptsReached,
}: ResendCodeCountdownProps): JSX.Element => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COUNTDOWN_SECONDS);

  useEffect(() => {
    setSecondsLeft(RESEND_COUNTDOWN_SECONDS);
  }, [resetSignal]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSecondsLeft(previousValue => {
        if (previousValue <= 1 || isMaxAttemptsReached) {
          window.clearInterval(intervalId);
          return 0;
        }

        return previousValue - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [secondsLeft, isMaxAttemptsReached]);

  const handleResendCode = (): void => {
    if (secondsLeft === 0) {
      // TODO: trigger resend OTP API here.
      // if (true) {
      toast.success({
        message: t('serviceRequestPage.resend.resendCodeSuccess'),
        placement: IS_DESKTOP ? 'topRight' : 'bottomRight',
      });
      // } else {
      //   toast.error({
      //     message: t('serviceRequestPage.resend.resendCodeError'),
      //     placement: 'bottomRight',
      //   });
      // }
      onResend();
      setSecondsLeft(RESEND_COUNTDOWN_SECONDS);
    }
  };

  const handleResendLinkClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (secondsLeft > 0) {
      event.preventDefault();
      return;
    }

    handleResendCode();
  };

  const resendLabel =
    secondsLeft === 0
      ? t('serviceRequestPage.resend.resendCode')
      : t('serviceRequestPage.resend.resendInSeconds', { seconds: secondsLeft });

  return (
    <Typography.Text style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR }}>
      {t('serviceRequestPage.resend.didNotReceive')}{' '}
      <Typography.Link
        underline
        style={{
          color: isMaxAttemptsReached ? kccThemeConstants.COLORS.TEXT_DISABLED : 'black',
          margin: 0,
          fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR,
          cursor: secondsLeft > 0 || isMaxAttemptsReached ? 'not-allowed' : 'pointer',
        }}
        onClick={handleResendLinkClick}
        disabled={isMaxAttemptsReached}
      >
        {resendLabel}
      </Typography.Link>
    </Typography.Text>
  );
};

export const ResendCodeCountdownMemoized = memo(ResendCodeCountdown);
